import { useState, useMemo, useEffect, type ReactNode } from "react";
import { useAuth } from "wasp/client/auth";
import { getAccountsByUser, getAccountGroupsByUser, getAccountChargesByUser, getTradingStrategiesByUser, getTptWalletContext, useQuery } from "wasp/client/operations";
import { startOfMonth } from "date-fns";
import { DateRangePicker, type DateRange } from "../client/components/DateRangePicker";
import { presetToDateRange } from "../client/utils/dateRange";
import { Building2, Upload, DollarSign } from "lucide-react";
import { Card, CardContent } from "../client/components/ui/card";
import { Button } from "../client/components/ui/button";
import { AccountGroupFilter } from "../trading-accounts/AccountGroupFilter";
import type { AccountFilterValue } from "../trading-accounts/AccountGroupFilter";
import StrategyMultiSelect from "../client/components/StrategyMultiSelect";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import { PageTransition } from "../client/components/ui/motion";
import useTranslation from "../client/hooks/useTranslation";
import { useIsDark } from "../client/components/ui/card-section-header";
import { CURRENCY } from "../shared/currency";
import { buildAccountCalendarEvents } from "./accountCalendarEvents";
import { chargeCalendarInstances } from "./chargeSchedule";
import PropFirmCalendar, { type CalendarEvent } from "./PropFirmCalendar";
import PropFirmMonthRecap from "./PropFirmMonthRecap";
import PayoutsAnalyticsSection from "./PayoutsAnalyticsSection";
import { PayoutsListSection } from "./PayoutsListSection";
import PropFirmImportModal from "./PropFirmImportModal";
import { BorderBeam } from "../client/components/ui/border-beam";
import AddPayoutModal from "./AddPayoutModal";
import AnimatedMetric from "../client/components/AnimatedMetric";
import { metricsContainerVariants, metricsItemVariants } from "../client/components/motionVariants";
import { motion } from "framer-motion";
import { WalletSection } from "./WalletSection";
import { computePfmKpis } from "./pfmPeriod";

interface KpiCardProps {
  label: string;
  value: string | ReactNode;
  sub?: string;
  highlight?: "positive" | "negative" | "neutral";
  withBeam?: boolean;
}

function KpiCard({ label, value, sub, highlight, withBeam }: KpiCardProps) {
  const color =
    highlight === "positive"
      ? "text-pnlPositive"
      : highlight === "negative"
      ? "text-pnlNegative"
      : "text-foreground";

  if (withBeam) {
    return (
      <div className="relative overflow-hidden p-[1.5px] rounded-xl">
        <Card variant="accent" className="relative z-10">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
            <p className={`text-xl font-bold font-mono ${color}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </CardContent>
        </Card>
        <BorderBeam duration={3} colorTo="hsl(var(--primary))" />
      </div>
    );
  }

  return (
    <Card variant="accent">
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
        <p className={`text-xl font-bold font-mono ${color}`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

interface PropFirmViewProps {
  externalAccounts?: any[];
  externalGroups?: any[];
  externalStrategies?: any[];
  externalIsLoading?: boolean;
  readOnly?: boolean;
}

export function PropFirmView({
  externalAccounts,
  externalGroups,
  externalStrategies,
  externalIsLoading,
  readOnly = false,
}: PropFirmViewProps) {
  const t = useTranslation();
  const { data: user } = useAuth();
  const isDark = useIsDark();
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    presetToDateRange(readOnly ? "all_time" : "last_30_days")
  );
  const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: "all" });
  const [strategyIds, setStrategyIds] = useState<string[]>([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddPayoutModal, setShowAddPayoutModal] = useState(false);

  useEffect(() => {
    if (!user?.id || readOnly) return;
    const timer = setTimeout(() => {
      localStorage.setItem(`onboarding_propFirmManagerVisited_${user.id}`, "true");
    }, 2500);
    return () => clearTimeout(timer);
  }, [user?.id, readOnly]);

  const { data: accountsData, isLoading: isLoadingInternal } = useQuery(getAccountsByUser, undefined, { enabled: !externalAccounts });
  const { data: accountGroupsData } = useQuery(getAccountGroupsByUser, undefined, { enabled: !externalAccounts });
  const { data: charges } = useQuery(getAccountChargesByUser, undefined, { enabled: !externalAccounts });
  const { data: strategies = [] } = useQuery(getTradingStrategiesByUser, undefined, { enabled: !externalAccounts });
  const { data: tptWalletContext } = useQuery(getTptWalletContext, undefined, { enabled: !readOnly });

  const accounts = (externalAccounts !== undefined ? externalAccounts : (accountsData as any)?.accounts ?? []) as any[];
  const groups = (externalGroups !== undefined ? externalGroups : (accountGroupsData ?? [])) as any[];
  const strategiesList = (externalStrategies !== undefined ? externalStrategies : strategies) as any[];
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : isLoadingInternal;

  const hiddenAccountsCount: number = externalAccounts !== undefined ? 0 : ((accountsData as any)?.hiddenAccountsCount ?? 0);

  const filteredAccounts = useMemo(() => {
    let all = (accounts ?? []) as any[];
    if (accountFilter.type === "account") all = all.filter((a) => a.id === accountFilter.id);
    else if (accountFilter.type === "group") all = all.filter((a) => a.groupId === accountFilter.id);
    if (strategyIds.length > 0) all = all.filter((a) => strategyIds.includes(a.strategyId));
    return all;
  }, [accounts, accountFilter, strategyIds]);

  const tptTemplateId = (tptWalletContext as any)?.propFirmTemplateId ?? null;
  const tptAccounts = (tptWalletContext as any)?.accounts ?? [];

  const start = dateRange.from ?? null;
  const end = dateRange.to ?? null;

  const kpis = useMemo(() => {
    const { bestRoi, ...rest } = computePfmKpis(filteredAccounts as any[], (charges ?? []) as any[], start, end);
    return { ...rest, bestRoiAccount: bestRoi };
  }, [filteredAccounts, charges, start, end]);

  const metricsAnimKey = `pfm-${dateRange.from?.getTime() ?? "all"}-${dateRange.to?.getTime() ?? "all"}-${accountFilter.type}-${accountFilter.type !== "all" ? (accountFilter as any).id ?? "" : ""}`;

  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    const events: CalendarEvent[] = [];

    for (const acc of filteredAccounts) {
      events.push(...buildAccountCalendarEvents(acc, CURRENCY));
    }

    const chargeList = (charges ?? []) as {
      id: string;
      amount: number;
      frequency: string;
      accountId?: string | null;
      name: string;
      createdAt: Date | string;
      startDate?: Date | string | null;
      endDate?: Date | string | null;
      billingDay?: number | null;
    }[];
    const now = new Date();

    for (const charge of chargeList) {
      if (charge.accountId) {
        const belongs = filteredAccounts.some((a) => a.id === charge.accountId);
        if (!belongs) continue;
      }
      // Même règle que les KPIs (chargeSchedule) : un abonnement fermé ne
      // projette plus aucun prélèvement après sa date de fin.
      const eventType = charge.frequency === "one_time" ? "expense" : "recurring";
      for (const { date, amount } of chargeCalendarInstances(charge, now)) {
        events.push({ date, type: eventType, amount, currency: CURRENCY, label: charge.name });
      }
    }

    return events;
  }, [filteredAccounts, charges]);

  if (isLoading) {
    return (
      <PageTransition className="container mx-auto py-10">
        <p className="text-sm text-muted-foreground">{t.common.loading}</p>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="container mx-auto py-10 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Building2 className="h-7 w-7 text-primary" />
          {t.propFirmManager.title}
        </h1>
        <p className="text-muted-foreground mt-1">{t.propFirmManager.subtitle}</p>
      </div>

      {!readOnly && hiddenAccountsCount > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 text-sm text-yellow-300">
          <span>
            {t.tradingAccounts.hiddenBanner.title.replace("{{count}}", String(hiddenAccountsCount))}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-4 shrink-0 border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/20"
            onClick={() => setShowPlanModal(true)}
          >
            {t.tradingAccounts.hiddenBanner.cta}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-start gap-3">
        <DateRangePicker value={dateRange} onChange={setDateRange} />
        <AccountGroupFilter
          accounts={(accounts ?? []) as any[]}
          groups={(groups ?? []) as any[]}
          value={accountFilter}
          onChange={setAccountFilter}
          className="w-[200px]"
        />
        {strategiesList.length > 0 && (
          <StrategyMultiSelect
            strategies={strategiesList}
            selectedIds={strategyIds}
            onChange={setStrategyIds}
            className="min-w-[180px]"
          />
        )}
        {!readOnly && (
          <div className="flex items-center gap-2 ml-auto">
            <Button size="sm" onClick={() => setShowAddPayoutModal(true)}>
              <DollarSign className="h-4 w-4 mr-2" />
              {t.propFirmManager.addPayoutBtn}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)}>
              <Upload className="h-4 w-4 mr-2" />
              {t.propFirmManager.importHistory}
            </Button>
          </div>
        )}
      </div>

      <motion.div
        key={metricsAnimKey}
        variants={metricsContainerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4"
      >
        <motion.div variants={metricsItemVariants}>
          <KpiCard
            label={t.propFirmManager.totalPayouts}
            value={<AnimatedMetric value={kpis.totalPayouts} format="decimal" decimals={0} prefix="$" />}
            highlight="positive"
            withBeam
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <KpiCard
            label={t.propFirmManager.totalExpenses}
            value={<AnimatedMetric value={kpis.totalExpenses} format="decimal" decimals={0} prefix="$" />}
            highlight="negative"
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <KpiCard
            label={t.propFirmManager.netProfit}
            value={<AnimatedMetric value={kpis.netProfit} format="decimal" decimals={0} prefix="$" />}
            highlight={kpis.netProfit >= 0 ? "positive" : "negative"}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <KpiCard
            label={t.propFirmManager.roiGlobal}
            value={<AnimatedMetric value={kpis.roi} format="percent" suffix="%" />}
            highlight={kpis.roi >= 0 ? "positive" : "negative"}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <KpiCard
            label={t.propFirmManager.activeAccounts}
            value={<AnimatedMetric value={kpis.activeCount} format="integer" />}
          />
        </motion.div>
        <motion.div variants={metricsItemVariants}>
          <KpiCard
            label={t.propFirmManager.bestRoi}
            value={
              kpis.bestRoiAccount
                ? <span className="flex items-baseline justify-between w-full"><AnimatedMetric value={kpis.bestRoiAccount.roi} format="percent" suffix="%" /><span className="text-sm font-normal text-muted-foreground">{kpis.bestRoiAccount.name}</span></span>
                : t.propFirmManager.noBestRoi
            }
            highlight={
              kpis.bestRoiAccount
                ? kpis.bestRoiAccount.roi >= 0
                  ? "positive"
                  : "negative"
                : undefined
            }
          />
        </motion.div>
      </motion.div>

      {tptTemplateId && !readOnly && (
        <WalletSection
          propFirmTemplateId={tptTemplateId}
          tptAccounts={tptAccounts}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <PropFirmCalendar
            events={calendarEvents}
            onMonthChange={setCalendarMonth}
            className="h-full"
          />
        </div>
        <div className="lg:col-span-1">
          <PropFirmMonthRecap events={calendarEvents} currentMonth={calendarMonth} />
        </div>
      </div>

      <PayoutsAnalyticsSection accounts={filteredAccounts as any} isDark={isDark} editable={!readOnly} />

      {!readOnly && <PayoutsListSection />}

      {!readOnly && (
        <>
          <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
          <PropFirmImportModal open={showImportModal} onOpenChange={setShowImportModal} />
          <AddPayoutModal
            open={showAddPayoutModal}
            onClose={() => setShowAddPayoutModal(false)}
            accounts={(accounts ?? []) as { id: string; name: string; status: string }[]}
            onSuccess={() => {}}
          />
        </>
      )}
    </PageTransition>
  );
}
